import React, { useMemo, useEffect, useState } from 'react';

import { AboutThisEventModal } from './AboutThisEventModal';
import type { StructuredContent } from '@/types';
import { AboutThisEventEmbedded } from './AboutThisEventEmbedded';
import { StructuredModuleRenderer } from './StructuredModuleRenderer';
import { MEDIA_QUERIES } from '@/components/_shared/constants';

export type AboutThisEventProps = {
  showFullEventOverview: boolean;
  structuredContent?: StructuredContent;
  handleCloseModal: () => void;
};

export const AboutThisEvent: React.FC<AboutThisEventProps> = React.memo(
  ({ showFullEventOverview, structuredContent, handleCloseModal }) => {
    const [isNarrowScreen, setIsNarrowScreen] = useState(() => {
      if (typeof window === 'undefined') return false;
      return window.innerWidth < MEDIA_QUERIES.DESKTOP_BREAKPOINT;
    });

    useEffect(() => {
      const handleResize = () => {
        const width = window.innerWidth;
        const narrow = width < MEDIA_QUERIES.DESKTOP_BREAKPOINT;
        setIsNarrowScreen(narrow);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    const memoizedStructuredContent = useMemo(() => structuredContent, [structuredContent]);

    const modalChildren = useMemo(() => {
      if (!memoizedStructuredContent?.modules) return null;
      return memoizedStructuredContent.modules.map((module, index) => (
        <StructuredModuleRenderer key={`modal-${index}`} module={module} isModal={true} />
      ));
    }, [memoizedStructuredContent?.modules]);

    const embeddedChildren = useMemo(() => {
      if (!memoizedStructuredContent?.modules) return null;
      return memoizedStructuredContent.modules.map((module, index) => (
        <StructuredModuleRenderer key={`embedded-${index}`} module={module} isModal={false} />
      ));
    }, [memoizedStructuredContent?.modules]);

    const aboutThisEventModal = isNarrowScreen && (
      <AboutThisEventModal visible={showFullEventOverview} onClose={handleCloseModal}>
        {modalChildren}
      </AboutThisEventModal>
    );

    return (
      <>
        {aboutThisEventModal}
        <AboutThisEventEmbedded visible={showFullEventOverview}>{embeddedChildren}</AboutThisEventEmbedded>
      </>
    );
  },
);

AboutThisEvent.displayName = 'AboutThisEvent';
